import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { Routes, Route, useParams} from 'react-router-dom';
import './App.scss';
import Banner from './components/app/Banner/Banner';
import Header from './components/app/Header/Header';
import Home from './components/pages/Home/Home';
import Footer from './components/reusable/footer/Footer';
import Navigation from './components/app/Navigation/Navigation';
import SearchResult from './components/pages/SearchResult/SearchResult';
import Record from './components/pages/Record/Record';
import TICGeneralInformation from "./components/pages/TICGeneralInformation/TICGeneralInformation";
import WorkingWithTIC from "./components/pages/WorkingWithTIC/WorkingWithTIC";
import SecurityGuidelines from "./components/pages/SecurityGuidelines/SecurityGuidelines";
import SecurityClassificationGuide from "./components/pages/SecurityClassificationGuide/SecurityClassificationGuide";
import ParkIndex from "./components/pages/ParkIndex/ParkIndex";
import HelpPage from "./components/pages/HelpPage/HelpPage";
import FaQs from "./components/pages/FAQs/FAQs";
import Archiving from "./components/pages/Archiving/Archiving";
import MissionStatement from "./components/pages/MissionStatement/MissionStatement";
import Teams from "./components/pages/Teams/Teams";
import AddRecord from './components/pages/Record/AddRecord';
import Chat from  "./components/pages/Chat/Chat";
import FileUpload from "./components/pages/FileUpload/FileUpload";
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import store from './store';
import Accessibility from "./components/pages/Accessibility/Accessibility";
import {createTheme, colors, ThemeProvider} from "@mui/material";
import TicRequests from './components/pages/Account/TicRequests';
import { RecoilRoot, useRecoilState } from "recoil";
import PendingTicNum from './components/pages/Admin/PendingTicNum';
import AssignedTicNum from './components/pages/Admin/AssignedTicNum';
import Admin from './components/pages/Admin/Admin';
import MyUploads from './components/pages/Account/MyUploads';
import UserUploads from './components/pages/Admin/UserUploads';
import OrderHistory from './components/pages/Account/OrderHistory';

import Orders from './components/pages/Admin/Orders';
import NewUsers from './components/pages/Admin/NewUsers';
import Announcements from './components/pages/Admin/Announcements';
import Statistics from './components/pages/Admin/Statistics';
import RequestTicNum from './components/pages/Resources/RequestTicNum';
import Login from './components/pages/login/Login';
import RequireAuth from './components/auth/RequireAuth';
import RequireAdmin from './components/auth/RequireAdmin';
import RequireNotPublic from './components/auth/RequireNotPublic';
import { OrderDownloadDialog } from './components/dialogs/Order-Download-Dialog';
import { AuthProvider } from './context/AuthContext';
import AuthCallback from './components/auth/AuthCallback';
import environment from "./environment/environment";
import axios from "axios";
import APIService from "./services/api.service";
import { CacheService } from "./common/services/cache.service";
import { tokenState } from './atoms/token';
import { parksState } from './atoms/parks';
import { cartState } from './atoms/cart';
import { userState } from "./atoms/user";
import { adminState } from './atoms/isAdmin';
import { Cart } from './models/assetResponse';
import { useAuth } from './hooks/useAuth';

library.add(fas);

const AppContent = () => {
  const { user, isAuthenticated } = useAuth();
  const [token, setToken] = useRecoilState(tokenState);
  const [, setParks] = useRecoilState(parksState);
  const [, SetCartState] = useRecoilState(cartState);
  const [, setUserInfo] = useRecoilState(userState);
  const [, setIsAdmin] = useRecoilState(adminState);
  
  useEffect(() => {
      if (environment.publicMode) {
        localStorage.removeItem('access_token');
        delete axios.defaults.headers.common['Authorization'];
        axios.defaults.withCredentials = false;
        setToken(null);
      }

      getParks();
    }, []);
  
  useEffect(() => {
    if (isAuthenticated && user) {
      setToken(user.access_token);
      getNPGUser();
      getCart(user.access_token);
    }
  }, [isAuthenticated, user]);

  const getCart = (accessToken: string) => {
    const url = `${environment.eticApiUrl}GetCart`;

    const apiService = new APIService<Cart>(
      axios,
      new CacheService(),
      url,
      accessToken
    );

    apiService
      .getAnyAsync()
      .then((res: any[]) => {
        SetCartState(res);
      })
      .catch((error: unknown) => {
        console.error("Failed to load the cart.", error);
      });
  };

  const getParks = () => {
    let url = `${environment.npgAPI}list?id=eticpark&format=JSON`;
    const apiService = new APIService<any>(axios, new CacheService, url, token);
  
    apiService.getAnyAsync().then((res:any) => {
      setParks(res);
    });
  }

  const getNPGUser = () => {
    if (environment.publicMode) {
      return;
    }
    
    const url = `${environment.npgApiUrl}user/current`;
    axios.get(url, { headers: {
      'Authorization': 'Bearer ' + localStorage.getItem(`access_token`)}, withCredentials: true}).then(res => {
        let roles: string[] = res.data.roles;
        setIsAdmin(roles.includes('etic_admin'));
        setUserInfo(res.data);
      });   
  }

  const theme = createTheme({
    palette: {
      secondary: {
        main: colors.blue[700],
      },
    }
  });
  
  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <Header />
        <Banner />
        <Navigation />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/authcallback" element={<AuthCallback />} />

          <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
          <Route path="/searchResults" element={<RequireAuth><SearchResult /></RequireAuth>} />
          <Route path="/record" element={<RequireAuth><Record /></RequireAuth>} />
          <Route path="/record/:Id" element={<RequireAuth><Record /></RequireAuth>} />
          <Route path="/upload" element={<RequireAuth><RequireNotPublic><AddRecord /></RequireNotPublic></RequireAuth>} />
          <Route path="/myUploads" element={<RequireAuth><RequireNotPublic><MyUploads /></RequireNotPublic></RequireAuth>} />
          <Route path="/orderHistory" element={<RequireAuth><RequireNotPublic><OrderHistory /></RequireNotPublic></RequireAuth>} />
          <Route path="/ticNumbers" element={<RequireAuth><RequireNotPublic><TicRequests /></RequireNotPublic></RequireAuth>} />
          <Route path="/pendingTic" element={<RequireAuth><RequireNotPublic><PendingTicNum /></RequireNotPublic></RequireAuth>} />
          <Route path="/assignedTic" element={<RequireAuth><RequireNotPublic><AssignedTicNum /></RequireNotPublic></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth><RequireNotPublic><RequireAdmin><Admin /></RequireAdmin></RequireNotPublic></RequireAuth>} />
          <Route path="/userUploads" element={<RequireAuth><RequireNotPublic><UserUploads /></RequireNotPublic></RequireAuth>} />
          <Route path="/orders" element={<RequireAuth><RequireNotPublic><Orders /></RequireNotPublic></RequireAuth>} />
          <Route path="/orders/:id" element={<RequireAuth><RequireNotPublic><Orders /></RequireNotPublic></RequireAuth>} />
          <Route path="/newUsers" element={<RequireAuth><RequireNotPublic><NewUsers /></RequireNotPublic></RequireAuth>} />
          <Route path="/statistics" element={<RequireAuth><RequireNotPublic><Statistics /></RequireNotPublic></RequireAuth>} />
          <Route path="/announcements" element={<RequireAuth><RequireNotPublic><Announcements /></RequireNotPublic></RequireAuth>} />
          <Route path="/requestTicNum" element={<RequireAuth><RequireNotPublic><RequestTicNum /></RequireNotPublic></RequireAuth>} />
          <Route path="/Overview" element={<RequireAuth><TICGeneralInformation /></RequireAuth>} />
          <Route path="/SecurityGuidelines" element={<RequireAuth><SecurityGuidelines /></RequireAuth>} />
          <Route path="/WorkingWithTIC" element={<RequireAuth><WorkingWithTIC /></RequireAuth>} />
          <Route path="/SecurityClassificationGuide" element={<RequireAuth><SecurityClassificationGuide /></RequireAuth>} />
          <Route path="/ParkIndex" element={<RequireAuth><ParkIndex /></RequireAuth>} />
          <Route path="/HelpPage" element={<RequireAuth><HelpPage /></RequireAuth>} />
          <Route path="/FaQs" element={<RequireAuth><FaQs /></RequireAuth>} />
          <Route path="/Archiving" element={<RequireAuth><Archiving /></RequireAuth>} />
          <Route path="/MissionStatement" element={<RequireAuth><MissionStatement /></RequireAuth>} />
          <Route path="/Teams" element={<RequireAuth><Teams/></RequireAuth>} />
          <Route path="/Chat" element={<RequireAuth><Chat/></RequireAuth>} />
          <Route path="/FileUpload" element={<RequireAuth><RequireNotPublic><FileUpload/></RequireNotPublic></RequireAuth>} />
          <Route path="/Accessibility" element={<RequireAuth><Accessibility/></RequireAuth>} />
        </Routes>
        <Footer />
      </Provider>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <RecoilRoot>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </RecoilRoot>
  );
};

export default App;